<template>
  <div>
    <popup-hint hint="bottling">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Bottling">

          <template v-slot:left v-if="incompleteCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                {{incompleteCount}} incomplete bottlings{{incompleteCount===1?'':'s'}}
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.completeStatus='0';filters.productId=null;filters.from=null;filters.to=null;filters.number=''})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>

          <template v-slot:right>
            <v-menu offset-y v-if="company.permissions.includes('config.write')" :close-on-content-click="false" nudge-bottom="5" left max-width="500">
              <template v-slot:activator="{ on }">
                <v-icon small v-on="on">mdi-tune-variant</v-icon>
              </template>
              <production-bottling-preferences @update="preferences=$event"/>
            </v-menu>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Bottling operations are recorded and managed here.
      </template>
    </popup-hint>

    <v-dialog v-model="showGraph" width="496">
      <component :is="graph" zoomed @close="showGraph=false" :from="graphFrom" :to="graphTo" disableTo/>
    </v-dialog>

    <purtrak-table name="bottling" objectType="bottlings" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="bottlings2" v-model="selected" @list="listBuilt"
                   fields="id,date,number,productId,productName,lot,duration,comments,completed,value,dutyPaid,
                           bottles,cases,pallets,laa,litres,alcoholName,tankName,strength,
                           laaLoss,percentLoss"
                   totalFields="totalBottles,totalCases,totalPallets,totalLAA,totalLitres,totalValue"
                   :totals.sync="totals"
                   :listFilters="listFilters"
                   @dblclick="bottling => $router.push({name:'ProductionBottlingEdit',params:{bottlingId:bottling.id}})"
                   :item-class="bottling => (bottling.completed ? '' : 'warning--text')"
                   @itemExpanded="({item, value}) => expandBottling(item, value)"
                   title="Bottling"
                   :print-info="{pageSize:'landscape',
                                 subTitle:[filters.from?dateDisplay:'',filters.completeStatus!=null?(filters.completeStatus=='1'?'Completed':'Incomplete'):'',filters.productId!=null?productName:'']}"
                   download-endpoint="bottlingsExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" auto-close :products="products"/>
        <filter-menu include-reset @reset="filters.completeStatus=null;filters.productId=null;filters.from=null;filters.to=null;categoryId=null;filters.lot='';filters.percentTolerance='0';filters.laaTolerance='0';filters.number=''">
          <v-list-item>
            <filter-search label="Lot" v-model="filters.lot"/>
          </v-list-item>
          <v-list-item>
            <filter-search label="Number" v-model="filters.number"/>
          </v-list-item>
          <v-list-item>
            <date-range :from.sync="filters.from" :to.sync="filters.to" v-model="dateDisplay" option="All" allow-all/>
          </v-list-item>
          <v-list-item v-if="preferences.bottleIncomplete">
            <v-autocomplete label="Completed" dense outlined :items="[{value:null,text:'All'},{divider:true},{value:'1',text:'Completed'},{value:'0',text:'Incomplete'}]" v-model="filters.completeStatus"/>
          </v-list-item>
          <v-list-item>
            <multi-level-select v-if="!!categories" label="Product" :menuData="categories" :first.sync="categoryId" :second.sync="filters.productId" outlined no-validation/>
            <v-autocomplete v-else label="Product" dense outlined :items="products" item-value="id" item-text="name" v-model="filters.productId"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.percentTolerance" true-value="1" false-value="0" label="Exceeding % loss tolerance"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.laaTolerance" true-value="1" false-value="0" label="Exceeding LAA loss tolerance"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.lot" close small class="ml-1" @click:close="filters.lot=''">Lot: {{filters.lot}}</v-chip>
          <v-chip v-show="filters.number!=''" close small class="ml-1" @click:close="filters.number=''">#: {{filters.number}}</v-chip>
          <v-chip v-show="filters.from!=null" close small class="ml-1" @click:close="filters.from=null;filters.to=null">{{dateDisplay}}</v-chip>
          <v-chip v-show="filters.completeStatus!=null" close small class="ml-1" @click:close="filters.completeStatus=null">{{filters.completeStatus=='1'?'Completed':'Incomplete'}}</v-chip>
          <v-chip v-show="filters.productId" close small class="ml-1" @click:close="filters.productId=null;categoryId=null">{{productName}}</v-chip>
          <v-chip v-show="filters.percentTolerance==='1'" close small class="ml-1" @click:close="filters.percentTolerance='0'">Exceeding % loss tolerance</v-chip>
          <v-chip v-show="filters.laaTolerance==='1'" close small class="ml-1" @click:close="filters.laaTolerance='0'">Exceeding LAA loss tolerance</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'ProductionBottlingEditNew'}"><v-list-item-title>Create bottling</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-menu :nudge-left="220" v-if="!$vuetify.breakpoint.mobile">
          <template v-slot:activator="{on:reports}">
            <v-list-item v-on="reports">
              <v-list-item-title>Reports and graphs</v-list-item-title>
              <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
            </v-list-item>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Bottilng',to:{name:'ProductionBottling'}}]},query:{to:'reports/production/ReportsBottlingActivity'}}">
              <v-list-item-title>Bottling activity</v-list-item-title>
              <v-list-item-action-text>Report</v-list-item-action-text>
            </v-list-item>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Bottilng',to:{name:'ProductionBottling'}}]},query:{to:'reports/production/ReportsBottlingLosses'}}">
              <v-list-item-title>Bottling losses</v-list-item-title>
              <v-list-item-action-text>Report</v-list-item-action-text>
            </v-list-item>
            <v-divider/>
            <v-list-item @click="getGraph('DashboardBottlingSummary')">
              <v-list-item-title>Bottling rate</v-list-item-title>
              <v-list-item-action-text>Graph</v-list-item-action-text>
            </v-list-item>
            <v-list-item @click="getGraph('DashboardBottlingHistory')">
              <v-list-item-title>Bottling history</v-list-item-title>
              <v-list-item-action-text>Graph</v-list-item-action-text>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <v-menu offset-y top>
          <template v-slot:activator="{ on }">
            <v-btn color="primary" outlined x-small style="position: absolute" class="mt-5 ml-10" v-on="on">Batch commands</v-btn>
          </template>
          <v-list dense>
            <v-list-item @click="printBottlingBatch()"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <v-divider/>
            <v-list-item @click="deleteMultiple=true;showDelete=true"><v-list-item-title>Delete</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:item.date="{item,value,printing}">
        {{item.date | formatDateTime}}
        <div v-if="!item.completed && !printing" class="text-caption"><em>INCOMPLETE</em></div>
      </template>
      <template v-slot:item.productName="{item,printing}">
        {{item.productName}}
        <div class="text-caption" v-if="!printing">{{item.comments}}</div>
      </template>
      <template v-slot:item.completed="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.dutyPaid="{value,printing}">
        <template v-if="printing">{{value?'Y':''}}</template>
        <v-icon v-else-if="value">mdi-check</v-icon>
      </template>
      <template v-slot:item.duration="{value}">
        {{value | duration}}
      </template>
      <template v-slot:item.value="{value}">
        {{value | currency}}
      </template>
      <template v-slot:item.strength="{value}">
        {{value | number(2)}}% alc./vol.
      </template>
      <template v-slot:item.bottles="{value}">
        {{value | number(0)}}
      </template>
      <template v-slot:item.cases="{value}">
        {{value | number(1)}}
      </template>
      <template v-slot:item.pallets="{value}">
        {{value | number(1)}}
      </template>
      <template v-slot:item.laa="{value}">
        {{value | number(3)}}
      </template>
      <template v-slot:item.litres="{value}">
        {{value | number(3)}}
      </template>
      <template v-slot:item.laaLoss="{value}">
        <div :class="{'warning--text':preferences.laaLoss && value >= preferences.laaLoss}">
          <v-icon small color="warning" v-if="preferences.laaLoss && value >= preferences.laaLoss">mdi-alert</v-icon>
          {{value | number(3)}}
        </div>
      </template>
      <template v-slot:item.percentLoss="{value}">
        <div :class="{'warning--text':preferences.percentLoss && value >= preferences.percentLoss}">
          <v-icon small color="warning" v-if="preferences.percentLoss && value >= preferences.percentLoss">mdi-alert</v-icon>
          {{value | number(1)}}%
        </div>
      </template>
      <template v-slot:item.lot="{item,value,printing}">
        <template v-if="printing">{{value}}</template>
        <v-hover v-else-if="item.lot" v-slot="{hover}">
          <a @click.stop="$router.push({name:'ReportsView',params:{back:[{text:'Bottling',to:{name:'ProductionBottling'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.lot,forward:0}})"
             :class="{'text-decoration-underline':hover}">{{item.lot}}</a>
        </v-hover>
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'ProductionBottlingEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}">
        <v-menu :nudge-width="150" offset-y>
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Bottling operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small>mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <v-list-item :to="{name:'ProductionBottlingEdit',params:{bottlingId:item.id}}"><v-list-item-title>Details</v-list-item-title></v-list-item>
            <v-list-item @click="printBottling(item.id,null,true)"><v-list-item-title>Print</v-list-item-title></v-list-item>
            <generate-qr-code type="Product" :id="item.productId" ask-quantity quantityUnits="cases" cases ask-lot :lots="[item.lot]"/>
            <v-divider/>
            <v-list-item :to="{name:'ReportsView',params:{back:[{text:'Bottling',to:{name:'ProductionBottling'}}]},query:{to:'reports/inventory/ReportsLot',id:item.id,lot:item.lot,forward:0}}"><v-list-item-title>Trace lot</v-list-item-title></v-list-item>
            <v-list-item v-if="audit" :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
            <template v-if="!readOnly">
              <v-divider/>
              <v-list-item @click="updateComments(item)"><v-list-item-title>Update comments</v-list-item-title></v-list-item>
              <v-list-item @click="bottling=item;deleteMultiple=false;showDelete=true"><v-list-item-title>Delete bottling</v-list-item-title></v-list-item>
            </template>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:body.append="{headers,printing}" v-if="!$vuetify.breakpoint.mobile">
        <tr :class="darkMode ? 'total-row-dark' : 'total-row'">
          <template v-for="header of headers">
            <td class="text-right" v-if="header.value==='bottles'">
              {{totals.totalBottles | number(0)}}
            </td>
            <td class="text-right" v-else-if="header.value==='cases'">
              {{totals.totalCases | number(1)}}
            </td>
            <td class="text-right" v-else-if="header.value==='pallets'">
              {{totals.totalPallets | number(1)}}
            </td>
            <td class="text-right" v-else-if="header.value==='laa'">
              {{totals.totalLAA | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='litres'">
              {{totals.totalLitres | number(3)}}
            </td>
            <td class="text-right" v-else-if="header.value==='value' && values">
              {{totals.totalValue | currency}}
            </td>
            <td v-else-if="!printing || header.print === undefined || header.print"/>
          </template>
        </tr>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <td :colspan="headers.length">
          <v-card class="my-4" :class="expansionPanelClass">
          <v-simple-table dense class="pl-16 text-caption" :class="expansionPanelClass" style="width:70%">
            <thead>
              <tr>
                <th :class="expansionPanelClass" width="40%">BOM</th>
                <th :class="expansionPanelClass" width="10%">Lot</th>
                <th class="text-right" :class="expansionPanelClass" width="30%">{{item.completed?'':'Pending '}}Quantity</th>
                <th class="text-right" :class="expansionPanelClass" width="20%" v-if="values && item.completed">Value</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(bottlingItem,index) of item.items" :key="index">
                <td class="text-caption">{{bottlingItem.name}}</td>
                <td class="text-caption">
                  <a @click="$router.push({name:'ReportsView',params:{back:[{text:'Bottling',to:{name:'ProductionBottling'}}]},query:{to:'reports/inventory/ReportsLot',id:bottlingItem.id,lot:bottlingItem.lot,forward:0}})">{{bottlingItem.lot}}</a>
                </td>
                <td class="text-caption text-right">{{bottlingItem.quantity | number(bottlingItem.precision)}} {{bottlingItem.units}}</td>
                <td class="text-caption text-right" v-if="values && item.completed">{{bottlingItem.value | currency}}</td>
              </tr>
            </tbody>
          </v-simple-table>
          </v-card>
        </td>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="deleteMultiple?'ALL selected bottlings':'this bottling'" @delete="performDelete(bottling.id)"/>
    <component :is="dialog" v-bind="dialogProperties" @done="dialog = null"/>
  </div>
</template>

<script>
import performDelete from '@/mixins/performDelete'
import createHierarchyList from '@/mixins/createHierarchyList'
import googleCharts from '@/mixins/googleCharts'
import {format,add} from 'date-fns'
import productionBottlingPrintBottling from '@/views/production/bottling/productionBottlingPrintBottling'
import ProductionBottlingPreferences from '@/views/production/bottling/ProductionBottlingPreferences'
import generateQrCode from '@/components/GenerateQrCode'

export default {
  name: 'ProductionBottling',
  mixins: [createHierarchyList,productionBottlingPrintBottling,googleCharts,performDelete],
  components: {ProductionBottlingPreferences,generateQrCode},
  data() {
    return {
      incompleteCount: 0,
      showStatus: false,
      filters: {
        number: '',
        from: null,
        to: null,
        future: '1',
        productId: null,
        completeStatus: null,
        lot: '',
        percentTolerance: 0,
        laaTolerance: 0
      },
      categoryId: null,
      dateDisplay: '',
      bottling: {},
      products: [],
      categories: null,
      preferences: {},
      totals: {
        totalValue: 0,
        totalBottles: 0,
        totalCases: 0,
        totalPallets: 0,
        totalLAA: 0,
        totalLitres: 0
      },
      showDelete: false,
      deleteMultiple: false,
			dialog: null,
      dialogProperties: {},
      graphFrom: '',
      graphTo: '',
      graph: null,
      showGraph: false,
      defaultColumns: [{ value: 'date', visible:true},
                       { value: 'number', visible:true},
                       { value: 'productName', visible:true},
                       { value: 'alcoholName', visible:false},
                       { value: 'tankName', visible:false},
                       { value: 'comments', visible:false},
                       { value: 'completed', visible:false},
                       { value: 'lot' ,visible:true},
                       { value: 'duration' ,visible:false},
                       { value: 'dutyPaid' ,visible:false},
                       { value: 'strength', visible:false},
                       { value: 'cases', visible:true},
                       { value: 'bottles', visible:false},
                       { value: 'pallets', visible:false},
                       { value: 'laa', visible:false},
                       { value: 'litres', visible:false},
                       { value: 'value', visible:true},
                       { value: 'laaLoss', visible:false},
                       { value: 'percentLoss', visible:false}],
      headers: [{ text: 'DATE', value: 'date', width:'15%',sortable:true},
                { text: '#', value: 'number', width:'10%',sortable:true},
                { text: 'PRODUCT', value: 'productName', width:'25%',sortable:true},
                { text: 'SPIRIT', value: 'alcoholName', width:'25%',sortable:true},
                { text: 'TANK', value: 'tankName', width:'25%',sortable:true},
                { text: 'COMMENTS', value: 'comments', width:'25%',sortable:false},
                { text: 'COMPLETED', value: 'completed', width:'10%',sortable:true,align:'center'},
                { text: 'STRENGTH', value: 'strength', width:'15%',sortable:true,align:'right'},
                { text: 'LOT', value: 'lot', width:'10%',sortable:true},
                { text: 'DURATION', value: 'duration', width:'15%',sortable:true, omit:() => !this.preferences.bottleDuration},
                { text: 'DUTY PAID?', value: 'dutyPaid', width:'10%',sortable:true,align:'center'},
                { text: 'BOTTLES', value: 'bottles', width:'15%',sortable:true,align:'right'},
                { text: 'CASES', value: 'cases', width:'15%',sortable:true,align:'right'},
                { text: 'PALLETS', value: 'pallets', width:'15%',sortable:true,align:'right'},
                { text: 'LAA', value: 'laa', width:'15%',sortable:true,align:'right'},
                { text: 'LITRES', value: 'litres', width:'15%',sortable:true,align:'right'},
                { text: 'VALUE', value: 'value', width:'15%',sortable:true,align:'right', omit:() => !this.values},
                { text: 'LOSS (LAA)', value: 'laaLoss', width:'15%',sortable:true,align:'right'},
                { text: 'LOSS', value: 'percentLoss', width:'15%',sortable:true,align:'right'}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    values() {
      return this.company.permissions.includes('values');
    },
    audit() {
      return this.company.allowAudit && this.company.permissions.includes('audit');
    },
    productName() {
      return this.products.find(product => product.id === this.filters.productId)?.name;
    },
    listFilters() {
      const filters = [];
      if (this.filters.number)
        filters.push({field:'number', comparator:'contains', value:this.filters.number});
      if (this.filters.lot)
        filters.push({field:'lot', comparator:'eq', value:this.filters.lot});
      if (this.filters.from)
        filters.push({field:'date', comparator:'ge', value:`${this.filters.from} 00:00:00`});
      if (this.filters.to)
        filters.push({field:'date', comparator:'le', value:`${this.filters.to} 23:59:59`});
      if (this.filters.completeStatus == '1' || this.filters.completeStatus == '0')
        filters.push({field:'completed', comparator:'eq', value:this.filters.completeStatus == '1' ? 'true' : 'false'});
      if (this.filters.productId)
        filters.push({field:'productId', comparator:'eq', value:this.filters.productId});
      if (this.filters.percentTolerance === '1' && this.preferences.percentLoss)
        filters.push({field:'percentLoss', comparator:'ge', value:`${this.preferences.percentLoss}`});
      if (this.filters.laaTolerance === '1' && this.preferences.laaLoss)
        filters.push({field:'laaLoss', comparator:'ge', value:`${this.preferences.laaLoss}`});
      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.loading = true;
    const categoriesList = [];
    await Promise.all([
      this.$lazyListLoad(categoriesList, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(this.products, 'products2', 'id,upc,scc,name,category{id}', 100, [{field:'active', comparator:'eq', value:'true'},{field:'mixedPack', comparator:'ne', value:'true'}])
    ]);
    const results = await this.$apiCall(`query ProductionBottling {
                                              bottlingPreferences {
                                                bottleDuration,bottleIncomplete,percentLoss,laaLoss
                                              }
                                            }`,
                                        {});
    if (!results.error)
      this.preferences = results.data.bottlingPreferences;
    if (categoriesList.length) {
      const categories = this.createHierarchyList(categoriesList);
      for (const category of categories) {
        const sub = this.products.filter(product => product.category?.id === category.id);
        if (sub.length)
          category.menu = sub;
        else
          category.disabled = true;
      }
//        this.categories = [{name:'All products',id:null},{divider:true},...categories.filter(category => !category.disabled)];
      this.categories = [{name:'All products',id:null},{divider:true},...categories];
      const leftOver = this.products.filter(product => !product.category?.id);
      if (leftOver.length)
        this.categories.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
    }
    else
      this.products = [{name:'All products',id:null},{divider:true}, ...this.products];
    await this.getStatus();
    this.loading = false;
    this.graphTo = format(new Date(), 'yyyy-MM-dd');
    this.graphFrom = format(add(new Date(), {years:-2}), 'yyyy-MM-dd');

    if (!['ProductionBottlingEdit', 'ProductionBottlingEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.incompleteCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    scan({type, id, lot}) {
      if (type === 'Product')
        Object.assign(this.filters, {completeStatus:null,productId:id,from:null,to:null,categoryId:null,lot});
    },
    async listBuilt({list}) {
      if (this.filters.productId && list.edges.length === 1) {
        const bottling = list.edges[0].node;
        await this.expandBottling(bottling, true);
        this.$refs.table.expanded.push(bottling);
      }
    },
    async getStatus() {
      const results = await this.$apiCall(`query ProductionBottling {
                                             bottlingsIncomplete
                                          }`);
      if (!results.error)
        this.incompleteCount = results.data.bottlingsIncomplete;
    },
    async getGraph(name) {
      await this.loadGoogleCharts();
      this.graph = () => import(`@/views/dashboard/${name}`);
      this.showGraph = true;
    },
    async expandBottling(item, value) {
      if (value && !item.items) {
        this.loading = true;
        const results = await this.$apiCall(`query ProductionBottling($id:ID!) {
                                                  bottling(id:$id) {
                                                    alcoholName,tankValue,tankId,tankLot,alcoholType,
                                                    bom{name,id,quantity,units,value,lot,precision}
                                                  }
                                                }`,
                                            {id:item.id});
        const bottling = results.data.bottling;
        bottling.bom.splice(0, 0, {name:bottling.alcoholName,id:bottling.tankId,quantity:bottling.alcoholType==='NonExcise'?item.litres:item.laa,precision:3,
                                   units:bottling.alcoholType==='NonExcise'?'litres':'LAA',value:bottling.tankValue,lot:bottling.tankLot});
        this.$set(item, 'items', bottling.bom);
        this.loading = false;
      }
    },
    updateComments(bottling) {
      this.dialogProperties = {item:bottling, operation:'bottlingUpdateComments'};
      this.dialog = () => import(`@/views/production/ProductionUpdateComments`);
    },
    async printBottlingBatch() {
      const lines = [];
      let n = this.selected.length;
      for (const bottling of this.selected) {
        lines.push(... await this.printBottling(bottling.id, null, false));
        if (--n)
          lines.push(`<div class="page-break"/>`);
      }
      this.printHtml('Bottling',lines.join(''));
    }
  }
}
</script>